// Zeiträume der Berichte (Agent 7A): Namen wie „Diese Woche“, daraus Tage in Berlin (JJJJ-MM-TT, `bis` einschließlich). Gespeicherte
// Berichte halten den Namen, nicht das Datum, damit ein Bericht morgen wieder „heute“ zeigt.
import { datumText, heute, tagPlus, wochentag } from "../werkzeuge/zeit";

export type ZeitraumArt =
  | "heute" | "gestern" | "diese_woche" | "letzte_woche" | "dieser_monat" | "letzter_monat"
  | "dieses_quartal" | "letztes_quartal" | "dieses_jahr" | "letztes_jahr" | "gesamt" | "eigen";

export interface ZeitraumWahl {
  art: ZeitraumArt;
  von?: string;
  bis?: string;
}

export interface Tage {
  von: string;
  bis: string;
}

export const ZEITRAUM_GRUPPEN: { titel: string; eintraege: { art: ZeitraumArt; label: string }[] }[] = [
  {
    titel: "Aktuell",
    eintraege: [
      { art: "heute", label: "Heute" },
      { art: "diese_woche", label: "Diese Woche" },
      { art: "dieser_monat", label: "Dieser Monat" },
      { art: "dieses_quartal", label: "Dieses Quartal" },
      { art: "dieses_jahr", label: "Dieses Jahr" },
    ],
  },
  {
    titel: "Davor",
    eintraege: [
      { art: "gestern", label: "Gestern" },
      { art: "letzte_woche", label: "Letzte Woche" },
      { art: "letzter_monat", label: "Letzter Monat" },
      { art: "letztes_quartal", label: "Letztes Quartal" },
      { art: "letztes_jahr", label: "Letztes Jahr" },
    ],
  },
  { titel: "Weitere", eintraege: [{ art: "gesamt", label: "Gesamte Zeit" }] },
];

export const ZEITRAUM_NAMEN: Record<ZeitraumArt, string> = {
  heute: "Heute", gestern: "Gestern", diese_woche: "Diese Woche", letzte_woche: "Letzte Woche", dieser_monat: "Dieser Monat",
  letzter_monat: "Letzter Monat", dieses_quartal: "Dieses Quartal", letztes_quartal: "Letztes Quartal", dieses_jahr: "Dieses Jahr",
  letztes_jahr: "Letztes Jahr", gesamt: "Gesamte Zeit", eigen: "Eigener Zeitraum",
};

/** Beginn der „gesamten Zeit“: vor dem ersten Eintrag im CRM (Abgleich ab Juli 2024). */
export const ANFANG_ALLER_ZEIT = "2024-01-01";

const zwei = (n: number) => String(n).padStart(2, "0");
const tag = (j: number, m: number, t: number) => `${j}-${zwei(m)}-${zwei(t)}`;
const letzterTagImMonat = (j: number, m: number) => new Date(Date.UTC(j, m, 0)).getUTCDate();
const teile = (d: string) => d.split("-").map(Number) as [number, number, number];

function montag(d: string): string {
  const w = wochentag(d); // 0 = Sonntag
  return tagPlus(d, -((w + 6) % 7));
}

/** Tage zu einem Namen, bezogen auf `heute_`. „Eigen“ braucht von und bis. */
export function tageFuer(wahl: ZeitraumWahl, heute_: string = heute()): Tage {
  const [j, m] = teile(heute_);
  switch (wahl.art) {
    case "heute": return { von: heute_, bis: heute_ };
    case "gestern": { const g = tagPlus(heute_, -1); return { von: g, bis: g }; }
    case "diese_woche": { const a = montag(heute_); return { von: a, bis: tagPlus(a, 6) }; }
    case "letzte_woche": { const a = tagPlus(montag(heute_), -7); return { von: a, bis: tagPlus(a, 6) }; }
    case "dieser_monat": return { von: tag(j, m, 1), bis: tag(j, m, letzterTagImMonat(j, m)) };
    case "letzter_monat": {
      const jj = m === 1 ? j - 1 : j;
      const mm = m === 1 ? 12 : m - 1;
      return { von: tag(jj, mm, 1), bis: tag(jj, mm, letzterTagImMonat(jj, mm)) };
    }
    case "dieses_quartal": case "letztes_quartal": {
      let q = Math.floor((m - 1) / 3);
      let jj = j;
      if (wahl.art === "letztes_quartal") {
        q -= 1;
        if (q < 0) { q = 3; jj -= 1; }
      }
      const m1 = q * 3 + 1;
      return { von: tag(jj, m1, 1), bis: tag(jj, m1 + 2, letzterTagImMonat(jj, m1 + 2)) };
    }
    case "dieses_jahr": return { von: tag(j, 1, 1), bis: tag(j, 12, 31) };
    case "letztes_jahr": return { von: tag(j - 1, 1, 1), bis: tag(j - 1, 12, 31) };
    case "gesamt": return { von: ANFANG_ALLER_ZEIT, bis: heute_ };
    case "eigen": {
      const von = wahl.von ?? heute_;
      const bis = wahl.bis ?? von;
      return von <= bis ? { von, bis } : { von: bis, bis: von };
    }
  }
  return { von: heute_, bis: heute_ };
}

/** Text für den Knopf: Name, bei eigenem Zeitraum die beiden Tage. */
export function zeitraumText(wahl: ZeitraumWahl, heute_: string = heute()): string {
  if (wahl.art !== "eigen") return ZEITRAUM_NAMEN[wahl.art];
  const z = tageFuer(wahl, heute_);
  return z.von === z.bis ? datumText(z.von) : `${datumText(z.von)} bis ${datumText(z.bis)}`;
}

export function istZeitraumArt(x: unknown): x is ZeitraumArt {
  return typeof x === "string" && x in ZEITRAUM_NAMEN;
}

/** Zeitraum aus einer gespeicherten Konfiguration (gültig oder Rückfall). */
export function wahlAus(roh: unknown, rueckfall: ZeitraumWahl): ZeitraumWahl {
  if (roh && typeof roh === "object") {
    const o = roh as Record<string, unknown>;
    if (istZeitraumArt(o.art)) {
      return { art: o.art, von: typeof o.von === "string" ? o.von : undefined, bis: typeof o.bis === "string" ? o.bis : undefined };
    }
  }
  return rueckfall;
}
